import { useCallback, useEffect, useRef, useState } from 'react';

import { differenceInMilliseconds, subSeconds, addMinutes, parseISO } from 'date-fns';
import { getAccessTokenExpiration, putAccessTokenExpiration } from 'src/service/accessToken';

import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { getTjenesterUrl } from '@helsenorge/framework-utils/hn-proxy-service';
import { setAllowNavigation } from '@helsenorge/framework-utils/hn-user';
import { error as logError } from '@helsenorge/framework-utils/logger';
import { HeaderFooterEvents } from '@helsenorge/framework-utils/web-component/constants';
import { useEventListener as useHnEventListener } from '@helsenorge/framework-utils/web-component/events';

import { lagreSkjema } from '../store/actions/actionCreators';
import { useAppDispatch, useAppSelector } from '../store/reducers';

interface UseEventListenerProps {
  timeSkjemaLoadedForUser: number;
}

interface UseEventListenerReturn {
  cancelTimers: () => void;
  refreshTokenIfExpiringSoon: () => Promise<void>;
}

const SUBTRACT_FROM_EXPIRATION_SECONDS = 60;
const SUBTRACT_TO_SET_NEW_TARGET_TIMESTAMP_MINUTES = 5;
const TOKEN_REFRESH_WINDOW_MS = 25 * 60 * 1000;

const useEventListener = ({ timeSkjemaLoadedForUser }: UseEventListenerProps): UseEventListenerReturn => {
  const kanMellomlagres = useAppSelector(state => state.refero.ui.kanMellomlagres);
  const dispatch = useAppDispatch();
  const webcompheader = document.querySelector('hn-webcomp-header');
  const [targetTimestamp, setTargetTimestamp] = useState<null | Date>(null);

  const dispatchTimerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canceledRef = useRef(false);
  const wasEverAuthorizedRef = useRef(isAuthorized());

  const performLogoutAction = useCallback((): void => {
    if (canceledRef.current) {
      return;
    }
    setAllowNavigation(true);
    const url = new URL(`/auth/signout`, getTjenesterUrl());
    window.location.assign(url.toString());
  }, []);

  // This function is used to cancel the timers when the function is called. used to stop the logout timer
  const cancelTimers = useCallback((): void => {
    canceledRef.current = true;
    if (dispatchTimerIdRef.current) {
      clearTimeout(dispatchTimerIdRef.current);
      dispatchTimerIdRef.current = null;
    }
    if (logoutTimerIdRef.current) {
      clearTimeout(logoutTimerIdRef.current);
      logoutTimerIdRef.current = null;
    }
  }, []);

  // This function fetches the access token expiration date from the server and parses it into a Date object
  const fetchTokenExpiration = useCallback(async (): Promise<Date | null> => {
    try {
      const response = await getAccessTokenExpiration();
      const iso = response?.Expire;
      if (!iso) {
        return null;
      }
      const dt = parseISO(iso);
      if (Number.isNaN(dt.getTime())) {
        return null;
      }
      return dt;
    } catch {
      return null;
    }
  }, []);

  const handleImmediateTokenCheck = useCallback(async (): Promise<void> => {
    const now = new Date();
    const expireDateTime = await fetchTokenExpiration();
    if (!expireDateTime) {
      performLogoutAction();
      return;
    }
    const remainingMs = differenceInMilliseconds(expireDateTime, now);
    const remainingMin = remainingMs / 60000;
    if (remainingMs <= 0) {
      performLogoutAction();
    } else if (remainingMin <= 5) {
      try {
        await dispatch(lagreSkjema({ timeSkjemaLoadedForUser, automatiskLagring: true }));
      } catch (e) {
        logError(`handleImmediateTokenCheck: save failed - ${e}`);
      }
      setTargetTimestamp(expireDateTime);
    } else {
      setTargetTimestamp(expireDateTime);
    }
  }, [fetchTokenExpiration, performLogoutAction, dispatch, timeSkjemaLoadedForUser]);

  const handleScheduledTokenCheck = useCallback(async (): Promise<void> => {
    const now = new Date();
    const expire = await fetchTokenExpiration();
    if (!expire) {
      performLogoutAction();
      return;
    }
    if (expire > addMinutes(now, SUBTRACT_TO_SET_NEW_TARGET_TIMESTAMP_MINUTES)) {
      cancelTimers();
      return;
    }
    try {
      await dispatch(lagreSkjema({ timeSkjemaLoadedForUser, automatiskLagring: true }));
    } catch {
      // save failed
      performLogoutAction();
      return;
    }
    const newDelayAfterDispatch = differenceInMilliseconds(expire, now);
    if (newDelayAfterDispatch <= 0) {
      performLogoutAction();
    } else {
      logoutTimerIdRef.current = setTimeout(() => {
        performLogoutAction();
      }, newDelayAfterDispatch);
    }
  }, [fetchTokenExpiration, performLogoutAction, dispatch, timeSkjemaLoadedForUser, cancelTimers]);

  useEffect(() => {
    if (!targetTimestamp) {
      return;
    }

    const now = new Date();
    const scheduledDispatchDate = subSeconds(targetTimestamp, SUBTRACT_FROM_EXPIRATION_SECONDS);
    const delayUntilDispatch = differenceInMilliseconds(scheduledDispatchDate, now);

    if (delayUntilDispatch <= 0) {
      (async (): Promise<void> => {
        try {
          await handleImmediateTokenCheck();
        } catch {
          performLogoutAction();
        }
      })();
      return;
    }
    dispatchTimerIdRef.current = setTimeout(() => {
      (async (): Promise<void> => {
        try {
          await handleScheduledTokenCheck();
        } catch {
          performLogoutAction();
        }
      })();
    }, delayUntilDispatch);

    return (): void => {
      if (dispatchTimerIdRef.current) {
        clearTimeout(dispatchTimerIdRef.current);
        dispatchTimerIdRef.current = null;
      }
      if (logoutTimerIdRef.current) {
        clearTimeout(logoutTimerIdRef.current);
        logoutTimerIdRef.current = null;
      }
    };
  }, [
    targetTimestamp,
    performLogoutAction,
    dispatch,
    timeSkjemaLoadedForUser,
    cancelTimers,
    handleImmediateTokenCheck,
    handleScheduledTokenCheck,
  ]);

  const checkAccessTokenExpiration = useCallback(async (): Promise<void> => {
    try {
      const expire = await fetchTokenExpiration();
      if (expire) {
        setTargetTimestamp(expire);
      }
    } catch (e) {
      logError(`checkAccessTokenExpiration: fetch failed - ${e}`);
    }
  }, [fetchTokenExpiration]);

  const refreshTokenIfExpiringSoon = useCallback(async (): Promise<void> => {
    if (targetTimestamp) {
      const remaining = differenceInMilliseconds(targetTimestamp, new Date());
      if (remaining > TOKEN_REFRESH_WINDOW_MS) {
        return;
      }
    }
    try {
      await putAccessTokenExpiration();
      const expire = await fetchTokenExpiration();
      if (expire) {
        setTargetTimestamp(expire);
      }
    } catch {
      // refresh failed
    }
  }, [targetTimestamp, fetchTokenExpiration]);

  const eventHandler = useCallback((): void => {
    if (kanMellomlagres) {
      if (canceledRef.current) {
        canceledRef.current = false;
      }
      checkAccessTokenExpiration();
    }
  }, [kanMellomlagres, checkAccessTokenExpiration]);

  // Add event listener to webcompheader
  useHnEventListener(HeaderFooterEvents.soonautosignout, webcompheader, eventHandler);

  // Check token expiration when tab becomes visible (e.g. wake from sleep, tab switch)
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && wasEverAuthorizedRef.current) {
        canceledRef.current = false;
        handleImmediateTokenCheck();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return (): void => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleImmediateTokenCheck]);

  return { cancelTimers, refreshTokenIfExpiringSoon };
};

export default useEventListener;
